import {useEffect, useRef} from 'react';
import store, { useAppSelector, useAppDispatch, rootActions } from '../data/store';
import {
    refresh, deepLinkAndStorageInit,
    initSortDataABTest, sendInitialAnalytics,
    initEqualHeight
} from '../services/list-page-provider';
import {IListItem} from '../interfaces/interfaces';
import { useWindowResizeObserver } from '../../../domain/animations/animations-provider';
import { getPurchasedData } from '../../membership/purchased-products/user-purchased-products';
import ListContent from '../content/content';
import MyRecommendedListContent from '../my-recommended/my-recommended-content';
import AddToCartBtn from '../panels/add-to-cart/add-to-cart-btn';
import ComparePopup from '../compare/compare-popup';
import { initButtonsABCDAbTest } from "../data/data-provider";
import { getABTest, sendABTestAnalytics } from '../../../domain/abtest/abtest-provider';

/**
 * List page app.
 */
const ListPage = () => {
    const dispatch = useAppDispatch();

    const products = useAppSelector(store => store.products);
    const layoutMode = useAppSelector(store => store.layoutMode);
    const filterTerm = useAppSelector(store => store.filterTerm);
    const currentPage = useAppSelector(store => store.currentPage);
    const itemsPerPage = useAppSelector(store => store.itemsPerPage);
    const sortType = useAppSelector(store => store.sortType);
    const sortProperty = useAppSelector(store => store.sortProperty);
    const sortOrder = useAppSelector(store => store.sortOrder);
    const showPriceSorting = useAppSelector(store => store.showPriceSorting);
    const isCompareVisible = useAppSelector(store => store.isCompareVisible);
    const isComparePopupVisible = useAppSelector(store => store.isComparePopupVisible);

    // https://wavesaudio.atlassian.net/browse/DSWEB-1934
    const isRecommended = useAppSelector(store => store.isRecommended);

    const [ width ] = useWindowResizeObserver();

    const containerRef = useRef<HTMLDivElement>(null);

    /**
     * ABTests ----------------------
     */
    useEffect(() => {
        (async () => {
            // https://wavesaudio.atlassian.net/browse/MRKT-25058
            const selected = initButtonsABCDAbTest();

            dispatch(rootActions.main({
                buttonsABCDAbTest: selected,
            }));
        })();
    }, [dispatch]);

    /**
     * set deep link, storage, and initial data on page load
     */
    useEffect(() => {

        (async () => {

            dispatch(rootActions.main({
                loading: true,
            }));

            const updated = {...store.getState()};

            // get data from deep link hash + session storage on page load
            // and push into the state on page load
            deepLinkAndStorageInit(updated, showPriceSorting);

            // bring purchased data if exists
            updated.purchased = await getPurchasedData();

            let shouldSort = true;

            const isAbTestsPath = window.location.pathname === '/specials' ||
                                           window.location.pathname === '/plugins';

            let abTest1Entered = false;

            // https://wavesaudio.atlassian.net/browse/DSWEB-5077
            // only visitors without "purchasedProducts" and with "recommendedProducts",
            // and only on specials and plugins pages in trending sort.
            const abtest1 = getABTest('RecsRFMOnly');
            if(abtest1?.isActive
                && isAbTestsPath
                && updated.purchased
                && updated.purchased.recommendedProducts?.length
                && (
                    !updated.purchased.purchasedProducts ||
                    updated.purchased.purchasedProducts.length <= 0)
            ) {
                sendABTestAnalytics(abtest1);
                shouldSort = abtest1.selected === 'yes';
                abTest1Entered = true;
            }
            else{
                shouldSort = !!updated.purchased?.recommendedProducts?.length ||
                             !!updated.purchased?.purchasedProducts?.length;
            }

            // https://wavesaudio.atlassian.net/browse/DSWEB-5086
            // only visitors with data in all 3: "purchasedProducts", "recommendedProducts", “recommendedProductsRFM“,
            // and only on specials and plugins pages in trending sort.
            const abtest2 = getABTest('RecsRFMAffinity');
            if(!abTest1Entered
                && abtest2?.isActive
                && isAbTestsPath
                && updated.purchased
                && updated.purchased.recommendedProducts?.length
                && updated.purchased.purchasedProducts?.length
                && updated.purchased.recommendedProductsRFM?.length
            ) {
                sendABTestAnalytics(abtest2);
                shouldSort = true;
                if(abtest2.selected === 'yes') {
                    updated.purchased.recommendedProducts = updated.purchased.recommendedProductsRFM || [];
                }
            }

            // apply jPlist on the state, and change deep link accordingly
            const res = refresh(updated, true);

            updated.products = (res.products || []) as IListItem[];
            updated.pagination = res.pagination;

            // update the state
            dispatch(rootActions.main(updated));

            // init a/b test
            if(shouldSort) {
                await initSortDataABTest(
                    updated,
                    (updatedProducts: IListItem[]) => {

                        updated.productsInitial = updatedProducts;

                        // apply jPlist on the state, and change deep link accordingly
                        const res = refresh(updated, true);

                        updated.products = (res.products || []) as IListItem[];
                        updated.pagination = res.pagination;
                    }
                );
            }

            // update the state
            dispatch(rootActions.refresh(updated));

            sendInitialAnalytics(
                updated.sortProperty,
                updated.products,
                updated.hideMyProducts
            );

            dispatch(rootActions.main({
                loading: false,
            }));

            window.setTimeout(async () => {
                await initEqualHeight(containerRef.current, width, layoutMode);
            }, 0);
        })();

    }, [dispatch, showPriceSorting, width, layoutMode]);

    /**
     * titles and taglines should have equal height in grid view
     */
    useEffect(() => {
        (async () => {
            await initEqualHeight(containerRef.current, width, layoutMode);
        })();
    }, [
        // update on every jPList action changes,
        // on document width change, and on products change
        width, products,
        layoutMode, filterTerm,
        currentPage, itemsPerPage,
        sortType, sortProperty, sortOrder,
        isRecommended,
    ]);

    return (
        <div className="text-base text-gray-700 my-4" ref={ containerRef }>
            <AddToCartBtn />
            { !isRecommended && <ListContent products={ products } width={ width } hasPanels={true} /> }
            { isRecommended && <MyRecommendedListContent products={ products } width={ width } /> }
            { isCompareVisible && isComparePopupVisible && <ComparePopup /> }
        </div>
    )
};

export default ListPage;